import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Atom, Zap, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const Hydrogen = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/hydrogen"]?.title} description={seoData["industries/hydrogen"]?.description} path="/industries/hydrogen" keywords={seoData["industries/hydrogen"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Hydrogen & New Energy"
        description="Coating and corrosion protection for hydrogen production, storage, and distribution facilities. Chemical-resistant coatings and infrastructure repair for the emerging hydrogen energy sector."
        backgroundKey="hydrogen"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Hydrogen embrittlement of steel components and vessels',
              'Electrolyzer and reformer corrosion',
              'High-pressure storage vessel protection requirements',
              'Cooling and utility system corrosion',
              'Concrete foundation degradation',
              'Regulatory compliance for hydrogen facility coatings'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-violet-50 rounded-lg">
                <Atom className="text-violet-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-violet-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Zap className="text-violet-600 mr-3 flex-shrink-0" size={22} />
                  Hydrogen-Compatible Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Coating systems specifically selected for hydrogen service environments. Epoxy and inorganic systems that resist hydrogen permeation and maintain integrity in hydrogen-rich atmospheres.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-violet-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Zap className="text-violet-600 mr-3 flex-shrink-0" size={22} />
                  Electrolyzer & Process Equipment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Corrosion protection for electrolyzers, reformers, heat exchangers, and compression equipment. Chemical-resistant coatings for alkaline and PEM electrolysis environments.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-violet-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Zap className="text-violet-600 mr-3 flex-shrink-0" size={22} />
                  Storage & Distribution Infrastructure
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Protective coatings for hydrogen storage vessels, piping systems, and distribution infrastructure. External corrosion protection for above-ground and buried infrastructure.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-violet-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Zap className="text-violet-600 mr-3 flex-shrink-0" size={22} />
                  Facility Infrastructure Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Concrete foundation repair, structural steel coating, floor systems, and utility infrastructure protection for hydrogen production and fueling facilities.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Hydrogen & New Energy?</h2>
              <div className="space-y-3">
                {['Coating systems compatible with hydrogen service environments',
                  'Experience with emerging energy infrastructure',
                  'Full regulatory compliance documentation',
                  'Confined space and classified area certified crews',
                  'Corrosion engineering support for new construction and retrofit',
                  'WBENC certified contractor for diversity program compliance'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-violet-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Future Energy Ready</h3>
              <p className="text-violet-100 mb-6">BCS is positioned to serve the growing hydrogen and clean energy sector with proven coating and repair solutions adapted for hydrogen production, storage, and distribution.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/protective-coatings')}>
                View Protective Coatings <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Coating Solutions for Hydrogen Infrastructure</h2>
          <p className="text-blue-100 mb-8">Contact us to discuss corrosion protection for your hydrogen or clean energy project.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Hydrogen;
